import { FeatureFlagBool, BOOL_FEATURE_FLAGS } from './FeatureFlagBool';
import { FeatureFlagJson, JSON_FEATURE_FLAGS } from './FeatureFlagJson';
import { FeatureFlagNumber, NUMBER_FEATURE_FLAGS } from './FeatureFlagNumber';
import { FeatureFlagString, STRING_FEATURE_FLAGS } from './FeatureFlagString';
import {
  FeatureFlagOverrideEntry,
  FeatureFlagOverrideStore,
  readFeatureFlagOverrideStore,
  writeFeatureFlagOverrideStore,
} from './override';
import { FEATURE_FLAG_OVERRIDE_STORE_VERSION } from './override/featureFlagOverrideTypes';
import { useState } from 'react';
import { z } from 'zod';

export type FeatureFlagNames = FeatureFlagBool | FeatureFlagJson | FeatureFlagNumber | FeatureFlagString;

type LocalStorageFlagValue = boolean | number | string;

export type LocalStorageFlags = {
  readonly [flagName: string]: LocalStorageFlagValue | undefined;
};

type FeatureFlagLocalState = {
  readonly overrideStore: FeatureFlagOverrideStore;
  readonly setLocalStorageFeatureFlags: (flags: RawFeatureFlagMap | undefined) => LocalStorageFlags;
  readonly localStorageFeatureFlags: LocalStorageFlags;
};

const includesFeatureFlagName = (flagNames: readonly string[], value: string): boolean => flagNames.includes(value);
const booleanSchema = z.boolean();
const numberSchema = z.number();
const stringSchema = z.string();
const rawFeatureFlagMapSchema = z.record(z.string(), z.unknown());

/** Raw DevTools flag values. Each entry is parsed to its named flag contract before storage. */
export type RawFeatureFlagMap = z.input<typeof rawFeatureFlagMapSchema>;

const toLocalStorageFlags = (store: FeatureFlagOverrideStore): LocalStorageFlags => {
  return Object.fromEntries(
    store.entries.map((entry) => [
      entry.override.key,
      entry.override.type === 'JSON' ? JSON.stringify(entry.override.value) : entry.override.value,
    ])
  );
};

const toOverrideEntries = (flags: RawFeatureFlagMap | undefined): FeatureFlagOverrideEntry[] => {
  if (!flags) {
    return [];
  }

  const parsedFlags = rawFeatureFlagMapSchema.safeParse(flags);
  if (!parsedFlags.success) {
    return [];
  }

  return Object.entries(parsedFlags.data).flatMap(([flagName, value]) => {
    try {
      const createdAt = new Date().toISOString();

      const parsedBoolean = booleanSchema.safeParse(value);
      if (includesFeatureFlagName(BOOL_FEATURE_FLAGS, flagName) && parsedBoolean.success) {
        const entry: FeatureFlagOverrideEntry = {
          // SAFETY: BOOL_FEATURE_FLAGS membership proves flagName is a FeatureFlagBool.
          override: { type: 'BOOLEAN', key: flagName as FeatureFlagBool, value: parsedBoolean.data },
          source: 'DEVTOOLS',
          createdAt,
        };
        return [entry];
      }

      if (includesFeatureFlagName(JSON_FEATURE_FLAGS, flagName)) {
        const parsedString = stringSchema.safeParse(value);
        const parsedValue = parsedString.success ? JSON.parse(parsedString.data) : value;
        const entry: FeatureFlagOverrideEntry = {
          // SAFETY: JSON_FEATURE_FLAGS membership proves flagName is a FeatureFlagJson.
          override: { type: 'JSON', key: flagName as FeatureFlagJson, value: parsedValue },
          source: 'DEVTOOLS',
          createdAt,
        };
        return [entry];
      }

      if (includesFeatureFlagName(NUMBER_FEATURE_FLAGS, flagName)) {
        const parsedString = stringSchema.safeParse(value);
        const parsedValue = parsedString.success ? Number(parsedString.data) : value;
        const parsedNumber = numberSchema.safeParse(parsedValue);
        if (!parsedNumber.success || Number.isNaN(parsedNumber.data)) {
          return [];
        }
        const entry: FeatureFlagOverrideEntry = {
          // SAFETY: NUMBER_FEATURE_FLAGS membership proves flagName is a FeatureFlagNumber.
          override: { type: 'NUMBER', key: flagName as FeatureFlagNumber, value: parsedNumber.data },
          source: 'DEVTOOLS',
          createdAt,
        };
        return [entry];
      }

      const parsedString = stringSchema.safeParse(value);
      if (includesFeatureFlagName(STRING_FEATURE_FLAGS, flagName) && parsedString.success) {
        const entry: FeatureFlagOverrideEntry = {
          // SAFETY: STRING_FEATURE_FLAGS membership proves flagName is a FeatureFlagString.
          override: { type: 'STRING', key: flagName as FeatureFlagString, value: parsedString.data },
          source: 'DEVTOOLS',
          createdAt,
        };
        return [entry];
      }

      return [];
    } catch {
      return [];
    }
  });
};

const readInitialOverrideStore = (): FeatureFlagOverrideStore => {
  return readFeatureFlagOverrideStore();
};

export const useFeatureFlagLocal = (): FeatureFlagLocalState => {
  const [overrideStore, setOverrideStore] = useState<FeatureFlagOverrideStore>(readInitialOverrideStore);

  const setLocalStorageFeatureFlags = (flags: RawFeatureFlagMap | undefined) => {
    const nextStore: FeatureFlagOverrideStore = {
      version: FEATURE_FLAG_OVERRIDE_STORE_VERSION,
      entries: toOverrideEntries(flags),
    };
    writeFeatureFlagOverrideStore(nextStore);
    setOverrideStore(nextStore);
    return toLocalStorageFlags(nextStore);
  };

  return {
    overrideStore,
    setLocalStorageFeatureFlags,
    localStorageFeatureFlags: toLocalStorageFlags(overrideStore),
  };
};
